import { supabase } from '@/lib/supabase';
import type { UserProgress } from '@/types/progress';

export class ProgressService {
  private userId: string;

  constructor(userId: string) {
    if (!userId) {
      throw new Error('UserId is required for ProgressService');
    }
    this.userId = userId;
  }

  private async checkAuth() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authenticated session');
    }
    return session;
  }

  async getUserProgress(): Promise<UserProgress> {
    try {
      await this.checkAuth();

      // Add proper headers and prefer parameter
      const { data: existingProgress, error: fetchError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', this.userId)
        .single()
        .headers({
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        });

      // If we have existing progress and no error, return it
      if (existingProgress && !fetchError) {
        return existingProgress;
      }

      // If no data exists or there was an error, try upsert with ON CONFLICT
      const initialProgress = {
        user_id: this.userId,
        completed_sections: [],
        career_stage: null,
        total_coins: 0,
        total_experience: 0,
        level: 1,
        completed_challenges: 0,
        completed_paths: 0,
        achievements: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Use upsert with proper conflict handling
      const { data: newProgress, error: upsertError } = await supabase
        .from('user_progress')
        .upsert(initialProgress, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        })
        .select('*')
        .single();

      if (upsertError) {
        console.error('Error upserting progress:', upsertError);
        // If upsert fails, try one more time to get existing progress
        const { data: retryProgress, error: retryError } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', this.userId)
          .single();

        if (retryError) throw new Error('Failed to get or create user progress');
        return retryProgress || initialProgress;
      }

      return newProgress || initialProgress;
    } catch (error) {
      console.error('Progress service error:', error);
      throw error;
    }
  }

  async updateProgress(updates: Partial<Omit<UserProgress, 'id' | 'user_id' | 'created_at'>>): Promise<UserProgress> {
    try {
      await this.checkAuth();

      const { data, error } = await supabase
        .from('user_progress')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', this.userId)
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }

  // Helper methods for common updates
  async addCompletedSection(sectionId: string): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const completed_sections = [...new Set([...(current?.completed_sections || []), sectionId])];
    await this.updateProgress({ completed_sections });
  }

  async addAchievement(achievementId: string): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const achievements = [...new Set([...(current?.achievements || []), achievementId])];
    await this.updateProgress({ achievements });
  }

  async addCoins(amount: number): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const total_coins = (current?.total_coins || 0) + amount;
    await this.updateProgress({ total_coins });
  }

  async addExperience(amount: number): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const total_experience = (current?.total_experience || 0) + amount;
    await this.updateProgress({ total_experience });
  }

  async incrementCompletedChallenges(): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const completed_challenges = (current?.completed_challenges || 0) + 1;
    await this.updateProgress({ completed_challenges });
  }

  async incrementCompletedPaths(): Promise<void> {
    const { data: current } = await this.getUserProgress();
    const completed_paths = (current?.completed_paths || 0) + 1;
    await this.updateProgress({ completed_paths });
  }
}